import React, { useRef, useState } from "react";
import Modal from "react-modal";
import { useInView } from "framer-motion";
import { FaPlay, FaXmark } from "react-icons/fa6";
import { Link } from "react-router-dom";
import { getEmbedUrl, getYouTubeThumbnail } from "../../../utils/helper";
import "./VoiceOfStakeholders.css";

Modal.setAppElement("#root");

const VIDEOS = [
  {
    id: "stk-01",
    name: "Yogesh Jagatramka",
    role: "President, IBMA",
    association: "Indian Builders\u2019 Material Association",
    title:
      "Yogesh Jagatramka on How Bharat Buildcon 2026 is Empowering the Industry",
    videoId: "K5NeXhp1Enk",
  },
  {
    id: "stk-02",
    name: "Bhaskar Ramoliya",
    role: "Secretary, RHMA",
    association: "Rajkot Hardware Manufacturers Association",
    title:
      "RHMA Secretary Bhaskar Ramoliya on Bharat Buildcon \u2014 One Nation, One Expo",
    videoId: "FlWrj5Tzwwo",
  },
  {
    id: "stk-03",
    name: "Surender Kumar Vats",
    role: "Director, AIIFA",
    association: "All India Ironmongery & Fasteners Association",
    title:
      "Mr. Surender Kumar Vats on Bharat Buildcon \u2014 One Nation, One Expo",
    videoId: "K5NeXhp1Enk",
  },
  {
    id: "stk-04",
    name: "Manish Patel",
    role: "Chairman, NFC India Club",
    association: "NFC India Club",
    title:
      "Manish Patel on How Bharat Buildcon 2026 is Empowering the Industry",
    videoId: "bXcWXGjHvvc",
  },
  {
    id: "stk-05",
    name: "Bharat Buildcon",
    role: "Powered by JSW",
    association: "Ministry of Housing and Urban Affairs",
    title:
      "Bharat Buildcon \u2014 Powering India\u2019s Journey to a Global Building Materials Hub",
    videoId: "uWDEJMUoKao",
  },
  {
    id: "stk-06",
    name: "AIIFA Leadership",
    role: "Industry Partner",
    association: "AIIFA",
    title: "Bharat Buildcon 2026 | AIIFA Supports One Nation, One Expo",
    videoId: "JdkzFZ24rCs",
  },
];

const PARTICLE_COLORS = [
  "var(--hh-saffron-light)",
  "#c9c9c9",
  "var(--hh-green)",
];

const PARTICLES = Array.from({ length: 16 }).map((_, i) => ({
  id: `p-${i}`,
  left: `${(i * 6.3) % 100}%`,
  size: 4 + ((i * 7) % 6),
  duration: 14 + ((i * 5) % 12),
  delay: -(i * 2.1),
  drift: `${(i % 2 === 0 ? 1 : -1) * (18 + (i % 5) * 6)}px`,
  color: PARTICLE_COLORS[i % PARTICLE_COLORS.length],
}));

function PlayIcon() {
  return <FaPlay size={16} aria-hidden="true" />;
}

function CloseIcon() {
  return <FaXmark size={18} aria-hidden="true" />;
}

function VideoCard({ video, index, onPlay }) {
  const cardRef = useRef(null);
  const isVisible = useInView(cardRef, { once: true, amount: 0.18 });

  return (
    <article
      ref={cardRef}
      className={`vos-card ${isVisible ? "is-visible" : ""}`}
      style={{ "--vos-delay": `${(index % 3) * 0.09}s` }}
    >
      <div
        className="vos-card__stage"
        role="button"
        tabIndex={0}
        aria-label={`Play video: ${video.title}`}
        onClick={() => onPlay(video)}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onPlay(video)}
      >
        {video.videoId ? (
          <img
            src={getYouTubeThumbnail(video.videoId)}
            alt={video.title}
            className="vos-card__thumbnail"
          />
        ) : (
          <div className="vos-card__stage-pattern" />
        )}

        <span className="vos-card__play">
          <PlayIcon />
        </span>

        <div className="vos-card__scrim" aria-hidden="true" />
      </div>

      <p className="vos-card__title">{video.title}</p>
    </article>
  );
}

function VideoModal({ video, onClose }) {
  if (!video) return null;

  return (
    <Modal
      isOpen={!!video}
      onRequestClose={onClose}
      className="vos-modal__panel"
      overlayClassName="vos-modal"
      contentLabel={video.title}
    >
      <button
        className="vos-modal__close"
        onClick={onClose}
        aria-label="Close video"
      >
        <CloseIcon />
      </button>
      <div className="vos-modal__frame">
        {video.videoId ? (
          <iframe
            src={getEmbedUrl(video.videoId)}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <div className="vos-modal__placeholder">
            <PlayIcon />
            <span>Video coming soon</span>
          </div>
        )}
      </div>
      <div className="vos-modal__caption">
        <h3>{video.title}</h3>
      </div>
    </Modal>
  );
}

export default function VoiceOfStakeholders() {
  const [activeVideo, setActiveVideo] = useState(null);

  return (
    <section className="vos" id="voice-of-stakeholders">
      <div className="vos__aurora" aria-hidden="true">
        <span className="vos__blob vos__blob--saffron" />
        <span className="vos__blob vos__blob--green" />
        <span className="vos__blob vos__blob--blue" />
      </div>
      <div className="vos__weave" aria-hidden="true" />
      <div className="vos__particles" aria-hidden="true">
        {PARTICLES.map((p) => (
          <span
            key={p.id}
            className="vos__particle"
            style={{
              left: p.left,
              "--size": `${p.size}px`,
              "--dur": `${p.duration}s`,
              "--delay": `${p.delay}s`,
              "--drift": p.drift,
              "--dot-color": p.color,
            }}
          />
        ))}
      </div>

      <header className="vos__header">
        <div className="clean-breadcrumb-wrapper">
          <nav className="about-style-breadcrumb" aria-label="Breadcrumb">
            <Link to="/" className="bc-home-link">
              HOME
            </Link>
            <span className="bc-slash">/</span>
            <span className="bc-active-page">VOICE OF STAKEHOLDERS</span>
          </nav>
        </div>
        <span className="vos__eyebrow">
          <span className="vos__tricolor-dots">
            <span />
            <span />
            <span />
          </span>
          Voices from Bharat Buildcon 2026
        </span>
        <h2 className="vos__title">
          Voice of <span className="vos__title-accent">Stakeholders</span>
        </h2>
        <p className="vos__subtitle">
          Hear directly from the associations powering India&rsquo;s journey to
          becoming a global building-materials hub &mdash; One Nation, One Expo.
        </p>
      </header>

      <div className="vos__grid">
        {VIDEOS.map((video, i) => (
          <VideoCard
            key={video.id}
            video={video}
            index={i}
            onPlay={setActiveVideo}
          />
        ))}
      </div>

      {activeVideo && (
        <VideoModal video={activeVideo} onClose={() => setActiveVideo(null)} />
      )}
    </section>
  );
}
